import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { SOCIALS } from "@/components/SocialIcons";
import { Reveal } from "@/components/ui-bits";

export default function Footer() {
  const c = useContent();
  const socials = c.socials || {};
  const email = c.contact?.email;

  return (
    <footer data-testid="site-footer" className="relative border-t border-white/5 bg-[#070707]">
      <div className="mx-auto max-w-[1200px] px-6 py-16 md:py-20">
        <Reveal>
          <p className="text-xs tracking-[0.35em] text-crimson uppercase">One story at a time</p>
          <h2 className="mt-4 max-w-3xl font-display text-4xl leading-tight font-semibold sm:text-5xl">
            Build your <span className="gold-text italic">masterpiece</span> version.
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 items-start gap-12 md:grid-cols-3">
          <div>
            <p className="font-display text-lg font-bold tracking-[0.2em]">
              <span className="gold-text">AVINASH KUMAR</span>
            </p>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-white/65">
              Dark psychology, storytelling and self-improvement by Avinash Kumar (@aesthetic_avii).
            </p>
            <p className="mt-6 text-xs tracking-[0.2em] text-white/35 uppercase">For collaborations and enquiries.</p>
            {email && (
              <a
                href={`mailto:${email}`}
                data-testid="footer-email-link"
                className="mt-2 inline-flex items-center gap-2 text-sm text-white/70 transition-colors duration-200 hover:text-gold"
              >
                <Mail size={14} className="text-gold/70" /> {email}
              </a>
            )}
          </div>
          <div>
            <p className="text-xs tracking-[0.3em] text-white/55 uppercase">Quick Links</p>
            <div className="mt-4 flex flex-col gap-3">
              {[
                ["Home", "/"],
                ["Community", "/community"],
                ["Brands", "/brands"],
                ["Music", "/music"],
                ["Grow As A Creator", "/creator"],
                ["My Story", "/about"],
                ["Contact", "/contact"],
              ].map(([label, to]) => (
                <Link
                  key={to}
                  to={to}
                  data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group flex w-fit items-center text-sm text-white/75 transition-all duration-200 ease-out select-none hover:translate-x-1 hover:text-white"
                >
                  <span className="mr-0 h-1 w-1 rounded-full bg-crimson opacity-0 transition-all duration-200 ease-out group-hover:mr-2 group-hover:opacity-100" />
                  {label}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs tracking-[0.3em] text-white/55 uppercase">Follow</p>
            <div className="mt-4 flex flex-wrap gap-3">
              {SOCIALS.filter((s) => socials[s.key]).map(({ key, label, Icon }) => (
                <a
                  key={key}
                  href={socials[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${key}`}
                  aria-label={label}
                  className="group flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white/75 transition-all duration-200 ease-out select-none hover:border-crimson hover:text-crimson hover:box-glow-red"
                >
                  <Icon size={17} className="transition-transform duration-200 ease-out group-hover:-translate-y-0.5" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-white/5 bg-black/40">
        <div className="mx-auto max-w-[1200px] px-6 py-5">
          <p data-testid="footer-copyright" className="text-xs text-white/55">
            &copy; 2026 Avinash Kumar
          </p>
        </div>
      </div>
    </footer>
  );
}
